import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function BestRestaurantsNearMePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Best Restaurants Near Me — Find & Pick Where to Eat Nearby | Hangrily"
        description="Find the best restaurants near you and actually pick one. Hangrily uses GPS to surface nearby restaurants with real photos, ratings & prices — then helps you swipe to a decision solo or with your group. Faster than Google Maps. Free."
        path="/best-restaurants-near-me"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Best Restaurants Near Me - Hangrily',
            description: 'Find and decide on the best restaurants near your location. GPS-powered discovery with swipe-to-decide interface.',
            url: 'https://hangrily.app/best-restaurants-near-me'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Best Restaurants Near Me', item: 'https://hangrily.app/best-restaurants-near-me' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How does Hangrily find restaurants near me?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'When you allow location access, Hangrily sends your GPS coordinates to Google Places API — the same database behind Google Maps. It returns restaurants within your chosen radius, sorted by relevance and rating. You then filter by cuisine, price, and mood before swiping.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is the restaurant data accurate and up to date?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Hangrily uses Google Places real-time data — the same source as Google Maps. Restaurant names, addresses, photos, ratings, price levels, and operating hours are pulled fresh with each search. If it\'s on Google Maps, it\'s on Hangrily.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I adjust the search distance?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. During the preferences step, set your distance anywhere from walking distance to a 30-minute drive. The app only shows restaurants within your chosen range. In rural areas, we recommend a wider radius to surface more options.'
                }
              },
              {
                '@type': 'Question',
                name: 'What if I\'m with a group and we all want different things?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'That\'s Hangrily\'s specialty. Share a session code with up to 10 friends — everyone swipes on the same set of nearby restaurants on their own phones. The app finds the restaurant with the highest combined agreement. It\'s democratic, fast, and nobody feels overruled.'
                }
              },
              {
                '@type': 'Question',
                name: 'How is this different from just using Google Maps?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Google Maps is a discovery tool — it shows you hundreds of options and leaves you to choose. Hangrily is a decision tool — it curates options based on your filters, presents them one at a time, and uses a swipe-based algorithm to pick a winner. It\'s the difference between browsing a library and having someone hand you the right book.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Best Restaurants Near Me</h1>
          <p className="hero-subtitle">
            Finding restaurants is easy. Choosing one? That's the hard part. We fix that.
          </p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')} style={{ marginTop: '24px' }}>
            Find Restaurants Near Me →
          </button>
        </section>

        <section className="content-section">
          <h2>The "200 Results" Problem</h2>
          <p style={{ maxWidth: '800px', margin: '0 auto 24px', lineHeight: 1.8, fontSize: '17px' }}>
            You search "restaurants near me" and get a wall of results. Google Maps shows a sea of red pins.
            Yelp has 47 curated lists. DoorDash has 300 options within 5 miles. You scroll for 15 minutes and 
            end up <strong>hungrier and less decided than when you started</strong>.
          </p>
          <p style={{ maxWidth: '800px', margin: '0 auto 24px', lineHeight: 1.8, fontSize: '17px' }}>
            The issue isn't finding restaurants — you've got more options than ever. The issue is <strong>choosing 
            one</strong> when you're overwhelmed, hungry, and possibly negotiating with other people who all 
            want different things. That's the gap Hangrily fills.
          </p>

          <div className="steps-grid" style={{ marginTop: '48px' }}>
            <div className="step-card">
              <span className="feature-emoji-lg" style={{ display: 'block', marginBottom: '12px' }}>📍</span>
              <h3>GPS-Powered Discovery</h3>
              <p>Allow location access and Hangrily pulls restaurants from Google Places within your chosen radius. Real data — names, photos, ratings, price, hours.</p>
            </div>
            <div className="step-card">
              <span className="feature-emoji-lg" style={{ display: 'block', marginBottom: '12px' }}>🎛️</span>
              <h3>Filter Before You Swipe</h3>
              <p>Narrow results by cuisine type, price range ($–$$$$), distance, and mood. Only see restaurants that match what you actually want tonight.</p>
            </div>
            <div className="step-card">
              <span className="feature-emoji-lg" style={{ display: 'block', marginBottom: '12px' }}>👆</span>
              <h3>Swipe, Don't Scroll</h3>
              <p>See one restaurant at a time — full photo, rating, distance, cuisine. Right to save, left to skip. No more comparison paralysis.</p>
            </div>
            <div className="step-card">
              <span className="feature-emoji-lg" style={{ display: 'block', marginBottom: '12px' }}>🏆</span>
              <h3>Winner in 90 Seconds</h3>
              <p>After 3 quick rounds of swiping, Hangrily picks the restaurant with the most love. Tap for directions and go.</p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>How Hangrily Compares to Other "Near Me" Tools</h2>
          <div className="comparison-table">
            <div className="compare-row compare-header">
              <div className="compare-feature">Feature</div>
              <div className="compare-them">Google Maps / Yelp</div>
              <div className="compare-us">Hangrily</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Shows nearby restaurants</div>
              <div className="compare-them">✅ Yes (200+ results)</div>
              <div className="compare-us">✅ Yes (filtered & curated)</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Helps you actually decide</div>
              <div className="compare-them">❌ You scroll endlessly</div>
              <div className="compare-us">✅ Swipe interface picks a winner</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Works with groups</div>
              <div className="compare-them">❌ Everyone stares at one phone</div>
              <div className="compare-us">✅ Up to 10 people swipe independently</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Couple mode</div>
              <div className="compare-them">❌ Not available</div>
              <div className="compare-us">✅ Blind swiping, mutual match</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Mood/occasion filters</div>
              <div className="compare-them">⚠️ Basic categories</div>
              <div className="compare-us">✅ Romantic, Adventurous, Quick, etc.</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Time to decision</div>
              <div className="compare-them">😩 15–30 min scrolling</div>
              <div className="compare-us">⚡ Under 2 min swiping</div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Explore by Cuisine Near You</h2>
          <p style={{ maxWidth: '700px', margin: '0 auto 32px', opacity: 0.7, textAlign: 'center' }}>
            Know what you're craving? Use Hangrily's cuisine filter to zero in:
          </p>
          <div className="use-case-grid">
            <Link to="/cuisine/italian" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍝</span>
              <h3>Italian Near Me</h3>
              <p>Pasta, wood-fired pizza, risotto, and wine. Find the highest-rated Italian spots in your area.</p>
            </Link>
            <Link to="/cuisine/mexican" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🌮</span>
              <h3>Mexican Near Me</h3>
              <p>Street tacos, burritos, mole, and margaritas. Discover authentic Mexican restaurants nearby.</p>
            </Link>
            <Link to="/cuisine/japanese" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍣</span>
              <h3>Japanese Near Me</h3>
              <p>Sushi bars, ramen shops, izakayas. Explore top-rated Japanese restaurants around you.</p>
            </Link>
            <Link to="/cuisine/chinese" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🥡</span>
              <h3>Chinese Near Me</h3>
              <p>Dim sum, Szechuan, hand-pulled noodles. Find the best Chinese food in your neighborhood.</p>
            </Link>
            <Link to="/cuisine/indian" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍛</span>
              <h3>Indian Near Me</h3>
              <p>Tikka masala, biryani, dosa, and tandoori. Discover Indian restaurants within walking distance.</p>
            </Link>
            <Link to="/cuisine/thai" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍜</span>
              <h3>Thai Near Me</h3>
              <p>Pad Thai, panang curry, papaya salad. Find Thai restaurants close to your current location.</p>
            </Link>
            <Link to="/cuisine/korean" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🥘</span>
              <h3>Korean Near Me</h3>
              <p>BBQ, bibimbap, fried chicken, tteokbokki. Explore Korean spots in your area.</p>
            </Link>
            <Link to="/cuisine/mediterranean" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🫒</span>
              <h3>Mediterranean Near Me</h3>
              <p>Hummus, falafel, shawarma, grilled kebabs. Find Mediterranean gems nearby.</p>
            </Link>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>Browse Restaurants by City</h2>
          <p style={{ maxWidth: '700px', margin: '0 auto 32px', opacity: 0.7, textAlign: 'center' }}>
            Hangrily works anywhere with Google Places coverage. See our most popular city pages:
          </p>
          <div className="internal-links-grid" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <Link to="/city/new-york" className="internal-link">New York</Link>
            <Link to="/city/los-angeles" className="internal-link">Los Angeles</Link>
            <Link to="/city/chicago" className="internal-link">Chicago</Link>
            <Link to="/city/houston" className="internal-link">Houston</Link>
            <Link to="/city/miami" className="internal-link">Miami</Link>
            <Link to="/city/san-francisco" className="internal-link">San Francisco</Link>
            <Link to="/city/seattle" className="internal-link">Seattle</Link>
            <Link to="/city/austin" className="internal-link">Austin</Link>
            <Link to="/city/denver" className="internal-link">Denver</Link>
            <Link to="/city/nashville" className="internal-link">Nashville</Link>
            <Link to="/city/boston" className="internal-link">Boston</Link>
            <Link to="/city/atlanta" className="internal-link">Atlanta</Link>
            <Link to="/city/washington-dc" className="internal-link">Washington DC</Link>
            <Link to="/city/new-orleans" className="internal-link">New Orleans</Link>
            <Link to="/city/portland" className="internal-link">Portland</Link>
            <Link to="/locations" className="internal-link">All 54 Cities →</Link>
          </div>
        </section>

        <section className="content-section">
          <h2>Questions About Finding Restaurants Nearby</h2>
          <div className="faq-list">
            <details className="faq-item">
              <summary>How does Hangrily find restaurants near me?</summary>
              <p>When you allow location access, Hangrily sends your GPS coordinates to Google Places API — the same database behind Google Maps. It returns restaurants within your chosen radius, sorted by relevance and rating. You then filter by cuisine, price, and mood before swiping.</p>
            </details>
            <details className="faq-item">
              <summary>Is the restaurant data accurate and up to date?</summary>
              <p>Yes. Hangrily uses Google Places real-time data — the same source as Google Maps. Restaurant names, addresses, photos, ratings, price levels, and operating hours are pulled fresh with each search. If it's on Google Maps, it's on Hangrily.</p>
            </details>
            <details className="faq-item">
              <summary>Can I adjust the search distance?</summary>
              <p>Absolutely. During the preferences step, set your distance anywhere from walking distance to a 30-minute drive. The app only shows restaurants within your chosen range. In rural areas, we recommend a wider radius to surface more options.</p>
            </details>
            <details className="faq-item">
              <summary>What if I'm with a group and we all want different things?</summary>
              <p>That's Hangrily's specialty. Share a session code with up to 10 friends — everyone swipes on the same set of nearby restaurants on their own phones. The app finds the restaurant with the highest combined agreement. It's democratic, fast, and nobody feels overruled.</p>
            </details>
            <details className="faq-item">
              <summary>How is this different from just using Google Maps?</summary>
              <p>Google Maps is a discovery tool — it shows you hundreds of options and leaves you to choose. Hangrily is a decision tool — it curates options based on your filters, presents them one at a time, and uses a swipe-based algorithm to pick a winner. It's the difference between browsing a library and having someone hand you the right book.</p>
            </details>
          </div>
        </section>

        <section className="content-section alt-bg">
          <div className="internal-links-section">
            <h2>More Ways to Decide Where to Eat</h2>
            <div className="internal-links-grid">
              <Link to="/where-should-i-eat" className="internal-link">Where Should I Eat?</Link>
              <Link to="/random-restaurant-picker" className="internal-link">Random Restaurant Picker</Link>
              <Link to="/date-night-restaurants" className="internal-link">Date Night Restaurants</Link>
              <Link to="/what-to-eat-tonight" className="internal-link">What to Eat Tonight</Link>
              <Link to="/couples-restaurant-picker" className="internal-link">Couples Picker</Link>
              <Link to="/group-restaurant-decision" className="internal-link">Group Decisions</Link>
              <Link to="/restaurant-wheel-alternative" className="internal-link">Wheel Alternative</Link>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Stop Scrolling. Start Eating.</h2>
          <p>Your next meal is within swiping distance. Find it in under 2 minutes.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Find Restaurants Now →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
